<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <router-link to="/research#trends">审计风控趋势</router-link>
      <span class="separator">/</span>
      <span class="current">基于图计算的反洗钱与反欺诈技术</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">审计风控趋势</span>
          <span class="tag">图计算</span>
          <span class="tag">技术白皮书</span>
        </div>
        <h1 class="article-title">基于图计算的反洗钱与反欺诈技术</h1>
        <p class="article-subtitle">图数据库 · 图算法 · 金融反洗钱 · 保险反欺诈 · 算法选型 · 落地架构</p>
        <div class="article-meta">
          <span>📅 2026-01-15</span>
          <span>⏱ 阅读约 20 分钟</span>
          <span>✍️ 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">🛡️</div>
        <div>
          <h3>导读</h3>
          <p>传统基于规则的反洗钱与反欺诈系统，面对日益复杂的跨机构、多层级资金转移和团伙欺诈时已力不从心。图计算技术通过对实体关系的网络化建模，天然擅长发现隐蔽的关联路径与异常拓扑。本文系统讲解图数据库与图计算引擎的核心原理、反洗钱/反欺诈典型场景、技术选型方法，并深度解析某股份制银行图计算反洗钱系统的完整落地案例。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、为什么传统方法不够用</a></li>
          <li><a href="#section2">二、图计算核心技术原理</a></li>
          <li><a href="#section3">三、反洗钱：资金流图谱分析</a></li>
          <li><a href="#section4">四、反欺诈：关系网络异常识别</a></li>
          <li><a href="#section5">五、技术选型与架构设计</a></li>
          <li><a href="#section6">六、案例：某股份制银行反洗钱系统</a></li>
          <li><a href="#section7">七、性能优化与部署建议</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、为什么传统方法不够用</h2>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">⚠️</div>
              <div>
                <h4>现实挑战</h4>
                <p>全球金融犯罪每年造成约 <strong>1.6万亿至3万亿美元</strong> 的损失，占全球GDP的2%-5%。我国银行业反洗钱合规成本逐年攀升，但仅依靠传统规则引擎的误报率高达 <strong>90%-99%</strong>，大量审计人员疲于应付，真实可疑线索却被淹没其中。</p>
              </div>
            </div>
          </div>

          <h3>传统规则引擎的三大局限</h3>
          <div class="limit-cards">
            <div class="limit-card" v-for="l in traditionalLimits" :key="l.title">
              <div class="limit-icon">{{ l.icon }}</div>
              <h4>{{ l.title }}</h4>
              <p class="limit-problem">{{ l.problem }}</p>
              <p class="limit-example">📌 {{ l.example }}</p>
            </div>
          </div>

          <h3>为什么图计算是答案</h3>
          <p>洗钱和欺诈的本质都是<strong>关系操纵</strong>：利用人与人、账户与账户、公司与公司之间的关联来掩盖非法资金流或虚构业务。图计算将这些实体和关系天然建模为节点（Node）与边（Edge），通过路径分析、社区发现、中心度计算等算法，能发现规则引擎无法提前枚举的复杂网络模式。</p>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">90%+</div>
              <div class="stat-label">传统规则引擎典型误报率，大量正常交易被误报</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">60%↓</div>
              <div class="stat-label">图计算可将误报率降低60%以上</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">10层+</div>
              <div class="stat-label">传统方法难以追踪超过3层的资金转移，图计算轻松穿透10层+</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">毫秒级</div>
              <div class="stat-label">现代图数据库支持毫秒级实时关系查询，满足交易拦截需求</div>
            </div>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、图计算核心技术原理</h2>

          <h3>2.1 图数据模型</h3>
          <p>反洗钱图谱通常使用<strong>属性图（Property Graph）</strong>模型，每个节点和边都可以携带丰富的属性信息。</p>

          <div class="model-cards">
            <div class="model-card" v-for="m in graphModels" :key="m.name">
              <div class="model-icon">{{ m.icon }}</div>
              <h4>{{ m.name }}</h4>
              <p>{{ m.desc }}</p>
              <div class="model-examples">
                <span v-for="ex in m.examples" :key="ex">{{ ex }}</span>
              </div>
            </div>
          </div>

          <h3>2.2 核心图算法</h3>
          <div class="algo-table-wrap">
            <table class="algo-table">
              <thead>
                <tr>
                  <th>算法类别</th>
                  <th>算法名称</th>
                  <th>反洗钱/反欺诈用途</th>
                  <th>计算复杂度</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in coreAlgorithms" :key="a.name">
                  <td class="algo-cat" :style="{ borderLeftColor: a.catColor }">{{ a.category }}</td>
                  <td class="algo-name">{{ a.name }}</td>
                  <td>{{ a.usage }}</td>
                  <td class="algo-complexity">{{ a.complexity }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>2.3 图嵌入与GNN</h3>
          <p>传统图算法是确定性的规则计算，而<strong>图神经网络（GNN）</strong>能从历史标注数据中学习节点/边的特征表示，通过端到端训练发现人工难以定义的复杂欺诈模式。</p>
          <div class="gnn-flow">
            <div class="gnn-step" v-for="(s, i) in gnnFlow" :key="i">
              <div class="gnn-step-num">{{ i + 1 }}</div>
              <div class="gnn-step-title">{{ s.title }}</div>
              <div class="gnn-step-desc">{{ s.desc }}</div>
              <div class="gnn-arrow" v-if="i < gnnFlow.length - 1">→</div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>组合使用效果最佳</h4>
              <p>实践表明，"图算法规则 + GNN模型 + 传统ML特征"三层融合的检测体系，比单一方法的AUC提升15-25%。图算法负责高可解释性的规则发现，GNN负责复杂模式挖掘，传统ML负责快速实时打分。</p>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、反洗钱：资金流图谱分析</h2>
          <p>洗钱通常经历三个阶段：<strong>放置（Placement）→ 离析（Layering）→ 归并（Integration）</strong>。图计算在离析阶段尤为有效，因为这一阶段涉及复杂的多层资金转移网络。</p>

          <h3>3.1 典型反洗钱图谱构建</h3>
          <div class="aml-entities">
            <div class="entity-card" v-for="e in amlEntities" :key="e.type">
              <div class="entity-icon" :style="{ background: e.color }">{{ e.icon }}</div>
              <div>
                <h4>{{ e.type }}（节点）</h4>
                <p>{{ e.desc }}</p>
                <div class="entity-props">关键属性：{{ e.props }}</div>
              </div>
            </div>
          </div>

          <h3>3.2 关键检测场景与算法</h3>
          <div class="scenario-cards">
            <div class="scenario-card" v-for="s in amlScenarios" :key="s.title">
              <div class="scenario-header" :style="{ background: s.color }">
                <span class="scenario-icon">{{ s.icon }}</span>
                <h4>{{ s.title }}</h4>
              </div>
              <div class="scenario-body">
                <p class="scenario-desc">{{ s.desc }}</p>
                <div class="scenario-algo"><strong>核心算法：</strong>{{ s.algo }}</div>
                <div class="scenario-signal"><strong>风险信号：</strong>{{ s.signal }}</div>
              </div>
            </div>
          </div>

          <h3>3.3 实时 vs 批量检测策略</h3>
          <div class="compare-blocks">
            <div class="compare-block realtime">
              <div class="compare-block-title">⚡ 实时图查询（交易拦截）</div>
              <ul>
                <li>交易发生时立即查询账户1-3度关联网络</li>
                <li>匹配高风险实体名单（黑名单传播）</li>
                <li>计算资金流转路径是否经过已知洗钱节点</li>
                <li>响应时间要求：≤100ms</li>
              </ul>
            </div>
            <div class="compare-block batch">
              <div class="compare-block-title">🔄 批量图计算（风险挖掘）</div>
              <ul>
                <li>T+1对全量历史数据运行社区发现算法</li>
                <li>识别异常资金环路和复杂分层结构</li>
                <li>输出高风险账户/团伙名单供人工核查</li>
                <li>计算周期：每日/每周离线任务</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、反欺诈：关系网络异常识别</h2>
          <p>欺诈犯罪的核心是<strong>虚构关系或滥用关系</strong>：使用同一设备注册多个账号、共享联系方式的账户集体申贷、虚假交易对手方等。图计算能从关系网络维度精准刻画欺诈团伙。</p>

          <h3>4.1 欺诈图谱核心关系</h3>
          <div class="fraud-relations">
            <div class="fraud-rel" v-for="r in fraudRelations" :key="r.relation">
              <div class="rel-icon">{{ r.icon }}</div>
              <div>
                <h4>{{ r.relation }}</h4>
                <p>{{ r.desc }}</p>
                <div class="rel-risk">风险强度：
                  <span class="risk-dot" v-for="n in r.risk" :key="n" :style="{ background: r.riskColor }"></span>
                  <span class="risk-dot-empty" v-for="n in (5 - r.risk)" :key="'e'+n"></span>
                </div>
              </div>
            </div>
          </div>

          <h3>4.2 团伙欺诈识别方法</h3>
          <div class="fraud-methods">
            <div class="fraud-method" v-for="m in fraudMethods" :key="m.name">
              <div class="method-badge" :style="{ background: m.color }">{{ m.tag }}</div>
              <h4>{{ m.name }}</h4>
              <p>{{ m.desc }}</p>
              <div class="method-result">🎯 {{ m.result }}</div>
            </div>
          </div>

          <h3>4.3 保险反欺诈专题</h3>
          <div class="insurance-scenarios">
            <div class="insurance-item" v-for="item in insuranceScenarios" :key="item.scene">
              <div class="ins-icon">{{ item.icon }}</div>
              <div>
                <h4>{{ item.scene }}</h4>
                <p>{{ item.desc }}</p>
                <div class="ins-algo">图算法：{{ item.algo }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、技术选型与架构设计</h2>

          <h3>5.1 图数据库选型</h3>
          <div class="tech-comparison">
            <div class="tech-option" v-for="db in graphDatabases" :key="db.name">
              <div class="tech-header" :style="{ background: db.color }">
                <span class="tech-name">{{ db.name }}</span>
                <span class="tech-type">{{ db.type }}</span>
              </div>
              <div class="tech-body">
                <div class="tech-pros">
                  <span class="pros-label">✅ 优势</span>
                  <ul>
                    <li v-for="p in db.pros" :key="p">{{ p }}</li>
                  </ul>
                </div>
                <div class="tech-cons">
                  <span class="cons-label">⚠️ 局限</span>
                  <ul>
                    <li v-for="c in db.cons" :key="c">{{ c }}</li>
                  </ul>
                </div>
                <div class="tech-use">📌 适用：{{ db.useCase }}</div>
              </div>
            </div>
          </div>

          <h3>5.2 整体技术架构</h3>
          <div class="arch-overview">
            <div class="arch-row" v-for="row in archRows" :key="row.name" :style="{ background: row.bg }">
              <div class="arch-row-name" :style="{ color: row.color }">{{ row.icon }} {{ row.name }}</div>
              <div class="arch-row-comps">
                <div class="arch-comp" v-for="c in row.comps" :key="c.name">
                  <div class="arch-comp-name">{{ c.name }}</div>
                  <div class="arch-comp-desc">{{ c.desc }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>选型核心原则</h4>
              <p>金融级反洗钱系统优先选择支持ACID事务、具备成熟企业版支持的图数据库（如TigerGraph Enterprise或Neo4j Enterprise）。追求极致查询性能的实时拦截场景可考虑NebulaGraph，而研究型/开发验证阶段可先用Neo4j社区版快速试验。</p>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、案例：某股份制银行反洗钱系统</h2>

          <div class="case-banner" style="background: linear-gradient(135deg, #0E1F52, #1F3F8F);">
            <div class="case-banner-info">
              <div class="case-banner-tag">金融行业 · 股份制银行</div>
              <h3>从规则引擎到图计算的反洗钱升级</h3>
              <p>可疑交易识别率提升 <strong>3倍</strong>，误报率下降 <strong>68%</strong>，复杂洗钱团伙穿透深度从 <strong>3层增至12层</strong></p>
            </div>
          </div>

          <div class="case-section">
            <h3>6.1 背景与痛点</h3>
            <div class="pain-points">
              <div class="pain-point" v-for="(p, i) in bankPainPoints" :key="i">
                <div class="pain-num">{{ i + 1 }}</div>
                <div>
                  <h4>{{ p.title }}</h4>
                  <p>{{ p.desc }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="case-section">
            <h3>6.2 图谱建设方案</h3>
            <div class="solution-timeline">
              <div class="timeline-item" v-for="(t, i) in buildTimeline" :key="i">
                <div class="timeline-marker" :style="{ background: t.color }">{{ t.month }}</div>
                <div class="timeline-content">
                  <h4>{{ t.title }}</h4>
                  <p>{{ t.desc }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="case-section">
            <h3>6.3 图谱规模与能力</h3>
            <div class="capability-stats">
              <div class="cap-stat" v-for="s in graphStats" :key="s.label">
                <div class="cap-value" :style="{ color: s.color }">{{ s.value }}</div>
                <div class="cap-label">{{ s.label }}</div>
              </div>
            </div>
          </div>

          <div class="case-section">
            <h3>6.4 量化成果</h3>
            <div class="results-grid">
              <div class="result-card" v-for="r in bankResults" :key="r.metric">
                <div class="result-value" :style="{ color: r.color }">{{ r.value }}</div>
                <div class="result-metric">{{ r.metric }}</div>
                <div class="result-detail">{{ r.detail }}</div>
              </div>
            </div>
          </div>

          <div class="case-section">
            <h3>6.5 典型洗钱链路发现示例</h3>
            <div class="chain-example">
              <div class="chain-step" v-for="(c, i) in chainExample" :key="i">
                <div class="chain-node" :style="{ background: c.color }">
                  <div class="chain-node-icon">{{ c.icon }}</div>
                  <div class="chain-node-label">{{ c.label }}</div>
                </div>
                <div class="chain-edge" v-if="i < chainExample.length - 1">
                  <div class="chain-amount">{{ chainExample[i].amount }}</div>
                  <div class="chain-arrow">→</div>
                </div>
              </div>
            </div>
            <p class="chain-note">图计算在秒级内完成跨12层、涉及47个账户的资金链路追踪，传统SQL查询需数小时甚至无法完成。</p>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、性能优化与部署建议</h2>

          <h3>7.1 大规模图的性能挑战</h3>
          <div class="perf-challenges">
            <div class="perf-challenge" v-for="c in perfChallenges" :key="c.title">
              <div class="perf-icon">{{ c.icon }}</div>
              <h4>{{ c.title }}</h4>
              <p>{{ c.desc }}</p>
            </div>
          </div>

          <h3>7.2 关键优化策略</h3>
          <div class="opt-strategies">
            <div class="opt-strategy" v-for="(s, i) in optStrategies" :key="i" :style="{ borderLeftColor: s.color }">
              <div class="opt-header">
                <span class="opt-tag" :style="{ background: s.color }">{{ s.tag }}</span>
                <h4>{{ s.title }}</h4>
              </div>
              <p>{{ s.desc }}</p>
              <div class="opt-effect">💡 效果：{{ s.effect }}</div>
            </div>
          </div>

          <h3>7.3 生产部署架构建议</h3>
          <div class="deploy-recs">
            <div class="deploy-rec" v-for="r in deployRecs" :key="r.title">
              <div class="rec-icon">{{ r.icon }}</div>
              <div>
                <h4>{{ r.title }}</h4>
                <p>{{ r.desc }}</p>
              </div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top:32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>结语：图计算是反金融犯罪的核心武器</h4>
                <p>图计算不是银弹，但它在处理关系复杂性方面具有其他技术无可替代的优势。金融机构应将图计算纳入反洗钱/反欺诈技术体系的核心组件，与规则引擎、机器学习协同作战，构建多层次、立体化的风险防控能力。古杉数据在多家金融机构成功落地了图计算风控平台，欢迎深度交流。</p>
              </div>
            </div>
          </div>
        </section>

      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>建设图计算风控系统？</h2>
        <p>古杉数据提供从图谱方案设计、图数据库选型到反洗钱/反欺诈系统落地的端到端技术服务</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约风控技术专家</router-link>
          <router-link to="/consulting/audit-risk-consulting" class="btn-outline">了解审计风控咨询</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'GraphComputingAML',
  data () {
    return {
      traditionalLimits: [
        { icon: '📏', title: '规则静态，难以对抗对抗性攻击', problem: '洗钱犯罪分子会针对已知规则调整行为（如化整为零、调整交易频率），规则引擎被绕过后几乎无能为力', example: '某行规则要求单笔≥5万触发预警，犯罪分子即用4.9万多次转账规避' },
        { icon: '🔍', title: '仅看单点，无法识别网络模式', problem: '规则引擎逐笔评估交易，无法感知账户在整个关系网络中的位置，不能判断"此账户是否处于已知洗钱网络的edge位置"', example: '一个中间账户每笔交易均合规，但其处于洗钱团伙的核心传导路径上' },
        { icon: '📈', title: '误报率居高，审计效率低下', problem: '为提高召回率而设置宽泛规则，导致90%以上的预警为误报，合规人员大量时间用于核查无效线索', example: '某大型银行每月产生150万条预警，人工仅能处理3万条，漏查率极高' }
      ],
      graphModels: [
        { icon: '🔵', name: '账户节点', desc: '银行账户、电子钱包、虚拟账户，携带开户时间、余额、活跃度等属性', examples: ['个人账户', '企业账户', '境外账户'] },
        { icon: '🟢', name: '实体节点', desc: '自然人、企业法人、受益所有人，携带身份信息、征信评分、关联关系等属性', examples: ['自然人', '企业法人', '关联方'] },
        { icon: '🟡', name: '交易边', desc: '转账、汇款、代付等资金流动，携带金额、时间、渠道、交易类型等属性', examples: ['转账', '汇款', '代付'] },
        { icon: '🔴', name: '关系边', desc: '开户关系、受益所有权、关联企业、共同设备等非资金关联', examples: ['开户关系', '股权关系', '设备关联'] }
      ],
      coreAlgorithms: [
        { category: '路径分析', catColor: '#1A73E8', name: '最短路径 / 所有路径搜索', usage: '追踪两个账户之间的所有资金传导路径，确认洗钱链路的完整回路', complexity: 'O(V+E)' },
        { category: '路径分析', catColor: '#1A73E8', name: '环路检测（Cycle Detection）', usage: '检测资金是否存在循环转账（A→B→C→A），识别"倒钱"洗钱手法', complexity: 'O(V+E)' },
        { category: '社区发现', catColor: '#10B981', name: 'Louvain / LPA社区发现', usage: '发现账户群体中的密集子团（疑似欺诈团伙），实现无监督团伙识别', complexity: 'O(V log V)' },
        { category: '社区发现', catColor: '#10B981', name: '弱连通分量（WCC）', usage: '快速划分孤立账户群体，识别无显式来源的资金孤岛', complexity: 'O(V+E)' },
        { category: '中心度算法', catColor: '#F59E0B', name: 'PageRank / 中介中心度', usage: '识别资金流转网络中的"关键中转点"账户，即洗钱枢纽账户', complexity: 'O(V²)' },
        { category: '相似度匹配', catColor: '#8B5CF6', name: '节点相似度（Jaccard）', usage: '识别与已知欺诈账户行为模式高度相似的新账户，辅助黑名单扩散', complexity: 'O(V²)' },
        { category: 'GNN模型', catColor: '#DC2626', name: 'GraphSAGE / GAT', usage: '端到端学习账户的图结构特征，对节点和交易进行欺诈概率评分', complexity: '依赖训练数据' }
      ],
      gnnFlow: [
        { title: '图构建', desc: '将账户关系和交易历史建模为属性图' },
        { title: '特征提取', desc: '每个节点聚合邻域的结构和属性特征' },
        { title: '消息传递', desc: 'GNN多跳传播，学习K阶邻居信息' },
        { title: '节点表示', desc: '生成账户的图嵌入向量' },
        { title: '风险评分', desc: '分类器输出欺诈概率' }
      ],
      amlEntities: [
        { icon: '💳', type: '账户', color: '#1A73E8', desc: '所有参与资金流转的账户，是图谱的核心节点', props: '账号、开户行、开户时间、余额、月均流水' },
        { icon: '👤', type: '自然人/企业', color: '#10B981', desc: '账户的实际控制人或受益所有人', props: '身份证/统一社会信用代码、注册地、实控人' },
        { icon: '📱', type: '设备/IP', color: '#F59E0B', desc: '账户登录使用的设备和IP，是识别一人多号的关键', props: '设备指纹、IMEI、IP地址、登录时间' },
        { icon: '🏢', type: '壳公司', color: '#8B5CF6', desc: '在离岸地注册的无实质业务公司，多层股权嵌套是常见洗钱工具', props: '注册地、业务范围、实控人、关联账户数' }
      ],
      amlScenarios: [
        { icon: '🔄', title: '分层转账（Layering）', color: 'linear-gradient(135deg, #1A73E8, #0E4EAD)', desc: '资金通过10层以上账户快速辗转，每层金额切割不同以规避监控', algo: '全路径搜索 + 路径深度统计', signal: '层数>5、单层停留<2小时、金额递减切割' },
        { icon: '🔀', title: '资金聚拢（Aggregation）', color: 'linear-gradient(135deg, #10B981, #065F46)', desc: '大量小额账户将资金汇聚到单一目标账户（蜗牛型图谱）', algo: '入度中心度分析 + 时间窗口聚合', signal: '目标账户入度异常高、来源账户批量新开' },
        { icon: '↩️', title: '循环转账（Loop）', color: 'linear-gradient(135deg, #F59E0B, #92400E)', desc: '资金在闭合路径内循环（A→B→C→A），制造虚假交易记录', algo: '图环路检测（DFS）', signal: '同一资金量在30日内完成循环回流' },
        { icon: '🌐', title: '壳公司穿透（Shell）', color: 'linear-gradient(135deg, #8B5CF6, #4C1D95)', desc: '通过多层空壳公司的股权/资金关系掩盖真实受益所有人', algo: '所有权穿透路径搜索 + 节点聚合', signal: '股权链深度>3层、注册地为避税天堂' }
      ],
      fraudRelations: [
        { icon: '📱', relation: '设备关联（同设备多号）', desc: '多个账户使用同一手机或电脑注册/登录，强烈提示账户被同一人控制', risk: 5, riskColor: '#DC2626' },
        { icon: '📞', relation: '联系方式关联', desc: '同一手机号或邮箱绑定多个账户，常见于批量注册欺诈账号', risk: 4, riskColor: '#EA580C' },
        { icon: '🏠', relation: '地址关联', desc: '共同住址或快递地址，在保险欺诈中用于识别虚假理赔团伙', risk: 3, riskColor: '#D97706' },
        { icon: '💳', relation: '银行卡关联', desc: '同一银行卡绑定多个支付账户，用于批量套现', risk: 4, riskColor: '#EA580C' },
        { icon: '🤝', relation: '受益人关联', desc: '多张保单共同受益人相同，是保险欺诈团伙的关键识别特征', risk: 5, riskColor: '#DC2626' }
      ],
      fraudMethods: [
        { tag: '社区发现', color: '#1A73E8', name: '团伙划分', desc: '使用Louvain或LPA算法对账户关系网络进行无监督社区发现，自动标记高密度子图作为疑似欺诈团伙', result: '发现人工无法感知的隐性团伙结构，某消金公司发现142个欺诈团伙' },
        { tag: '黑名单传播', color: '#10B981', name: '标签传播扩散', desc: '从已确认的欺诈账户出发，将欺诈标签沿图边传播至关联账户，实现黑名单扩散', result: '黑名单覆盖率提升3-5倍，提前识别尚未作案的风险账户' },
        { tag: '模式匹配', color: '#F59E0B', name: '子图同构匹配', desc: '将已知欺诈团伙的图结构（模板）与实时图进行同构搜索，发现结构相似的新欺诈团伙', result: '识别已知欺诈手法的变种，准确率达85%以上' }
      ],
      insuranceScenarios: [
        { icon: '🚗', scene: '汽车保险欺诈', desc: '虚构车祸、夸大维修费用。图计算分析：定损员→修理厂→理赔账户三角关系，识别异常高频合作', algo: '三角形计数 + 关系密度分析' },
        { icon: '🏥', scene: '医疗保险欺诈', desc: '虚假就诊、过度医疗。图计算分析：医院→医生→患者关系网络，识别批量开具相同诊断的异常模式', algo: '共现分析 + 异常密度检测' },
        { icon: '🏠', scene: '财产保险欺诈', desc: '虚假火灾、盗窃。图计算分析：受益人关系网络与历史理赔记录交叉，发现"专业理赔人"', algo: '路径分析 + 中心度计算' }
      ],
      graphDatabases: [
        { name: 'Neo4j', type: '原生图数据库', color: 'linear-gradient(135deg, #1A73E8, #0E4EAD)', pros: ['最成熟的图数据库生态', 'Cypher查询语言易学易用', '丰富的图算法库（GDS）'], cons: ['超大规模数据（百亿级）性能下降', '企业版授权成本较高'], useCase: '中小规模图（<50亿边），快速原型开发，企业级应用' },
        { name: 'NebulaGraph', type: '分布式图数据库', color: 'linear-gradient(135deg, #10B981, #065F46)', pros: ['支持百亿级节点/边', '毫秒级实时查询', '国内社区活跃，金融案例丰富'], cons: ['相对较新，生态不如Neo4j', '运维复杂度较高'], useCase: '大规模金融图谱，实时交易拦截场景' },
        { name: 'TigerGraph', type: '企业级图平台', color: 'linear-gradient(135deg, #F59E0B, #92400E)', pros: ['企业级金融场景最佳实践多', '内置多种图算法', '支持实时+批量混合计算'], cons: ['商业授权费用高', '国内支持团队有限'], useCase: '大型金融机构，预算充足，需要完整商业支持' },
        { name: 'GraphX/DGL', type: '大规模图计算框架', color: 'linear-gradient(135deg, #8B5CF6, #4C1D95)', pros: ['与Spark/PyTorch深度集成', '适合GNN训练', '超大规模批量计算'], cons: ['不支持实时查询', '需要较强工程能力'], useCase: '离线批量图挖掘，GNN模型训练' }
      ],
      archRows: [
        { name: '数据采集层', icon: '🗄️', bg: '#EBF3FE', color: '#1A73E8', comps: [{ name: '核心业务系统', desc: 'ERP、核心银行、信贷系统实时入图' }, { name: '外部数据', desc: '工商、法院、黑名单等第三方数据增强' }, { name: '设备/行为数据', desc: '登录日志、App指纹等行为信号' }] },
        { name: '图存储层', icon: '📊', bg: '#F0FFF4', color: '#10B981', comps: [{ name: '实时图数据库', desc: 'NebulaGraph/Neo4j存储关系图谱' }, { name: '历史数据仓库', desc: '全量历史交易图存储，支持离线分析' }, { name: '特征存储', desc: '节点/边特征缓存，供模型实时调用' }] },
        { name: '计算引擎层', icon: '⚙️', bg: '#FFFBEB', color: '#D97706', comps: [{ name: '实时图查询', desc: '毫秒级关系路径查询，支持交易拦截' }, { name: '批量图计算', desc: 'Spark/GraphX运行社区发现等离线算法' }, { name: 'GNN推理引擎', desc: '图神经网络实时打分服务' }] },
        { name: '应用服务层', icon: '🎯', bg: '#FDF4FF', color: '#8B5CF6', comps: [{ name: '实时拦截API', desc: '交易发起时调用，返回风险评分' }, { name: '预警工单系统', desc: '批量扫描结果推送到反洗钱工作台' }, { name: '图谱可视化', desc: '调查人员使用的交互式图谱探索工具' }] }
      ],
      bankPainPoints: [
        { title: '规则繁多但效果有限', desc: '历经多年积累超过2000条反洗钱规则，每月产生约80万条预警，合规人员可核查的不足5%，真实可疑线索淹没其中' },
        { title: '无法穿透多层资金链路', desc: '现有系统仅能追查3层资金转移，而监管机构发现的复杂洗钱案件平均涉及7-12层资金链路' },
        { title: '跨机构关联缺失', desc: '客户在多家银行开户的情况普遍，无法整合外部数据进行关联分析，导致大量分散账户逃脱监控' }
      ],
      buildTimeline: [
        { month: 'M1-2', color: '#4A90D9', title: '数据接入与图谱建设', desc: '完成核心银行系统、信贷系统数据接入，建立账户-实体-交易图谱，初始图规模：5亿节点、80亿边' },
        { month: 'M3-4', color: '#1F6FEB', title: '核心算法开发与测试', desc: '开发分层路径检测、循环转账检测、资金聚拢检测等核心规则，用2年历史数据进行回测验证' },
        { month: 'M5-6', color: '#0E4EAD', title: 'GNN模型训练与上线', desc: '利用历史已确认洗钱案例标注数据训练GraphSAGE模型，模型AUC达到0.91' },
        { month: 'M7-8', color: '#0A3A82', title: '生产上线与效果验证', desc: '完成与核心风控系统的集成，上线实时查询API和批量扫描任务，持续优化阈值' }
      ],
      graphStats: [
        { value: '12亿+', label: '图谱节点数', color: '#1A73E8' },
        { value: '180亿+', label: '图谱边数（关系）', color: '#10B981' },
        { value: '<50ms', label: '3度关联实时查询延迟', color: '#F59E0B' },
        { value: '12层', label: '最大资金链路穿透深度', color: '#8B5CF6' },
        { value: '200+', label: '图算法规则数量', color: '#DC2626' },
        { value: '日增5亿', label: '日新增边（交易记录）', color: '#0E1F52' }
      ],
      bankResults: [
        { metric: '可疑交易识别率', value: '提升3倍', detail: '发现的真实可疑线索数量提升3倍以上', color: '#1A73E8' },
        { metric: '误报率', value: '下降68%', detail: '由90%降至29%，合规人员效率大幅提升', color: '#10B981' },
        { metric: '穿透深度', value: '3层→12层', detail: '复杂分层洗钱链路完整追溯', color: '#F59E0B' },
        { metric: '案件发现时效', value: '提前2-4个月', detail: '相比人工排查，提前2-4个月发现可疑迹象', color: '#8B5CF6' }
      ],
      chainExample: [
        { icon: '🏢', label: '境外壳公司A', color: '#1A73E8', amount: '500万' },
        { icon: '💳', label: '香港账户X1', color: '#1F6FEB', amount: '—' },
        { icon: '💳', label: '香港账户X2', color: '#2563EB', amount: '490万' },
        { icon: '💳', label: '国内账户B1', color: '#3B82F6', amount: '—' },
        { icon: '💳', label: '国内账户B2-B5', color: '#60A5FA', amount: '切割为4笔' },
        { icon: '🏪', label: '壳体电商商户', color: '#93C5FD', amount: '合计484万' },
        { icon: '👤', label: '目标个人账户', color: '#0E1F52', amount: '最终汇入' }
      ],
      perfChallenges: [
        { icon: '🌐', title: '超级节点（Super Node）问题', desc: '某些热点账户可能有数百万条边，遍历时引发计算爆炸，需要特殊处理策略' },
        { icon: '⚡', title: '实时写入与查询并发冲突', desc: '高频交易数据持续写入图库，与实时多跳查询存在锁争用，影响查询延迟' },
        { icon: '💾', title: '内存资源消耗', desc: '图算法（如PageRank）需要将整个图加载到内存，对内存资源要求极高' }
      ],
      optStrategies: [
        { tag: '索引优化', color: '#1A73E8', title: '精准索引设计', desc: '对账号、时间戳、风险标签等高频查询字段建立复合索引；使用属性图的原生图存储（Native Graph Storage）避免Index-Free Adjacency失效', effect: '3度查询延迟从200ms降至30ms' },
        { tag: '超级节点', color: '#10B981', title: '超级节点截断策略', desc: '对度数超过阈值（如10万）的超级节点，在查询时进行动态截断或只返回采样邻居，防止查询爆炸', effect: '避免因超级节点导致查询超时崩溃' },
        { tag: '分层存储', color: '#F59E0B', title: '冷热数据分层', desc: '近90天交易边存入实时图数据库（热数据），历史数据存入分析型图存储（冷数据），按需联邦查询', effect: '热数据图规模缩小80%，查询性能翻倍' },
        { tag: '批量优化', color: '#8B5CF6', title: 'BSP/异步计算模型', desc: '离线图算法采用Bulk Synchronous Parallel模型，使用GraphX/DGL在分布式集群上并行执行，支持百亿级图', effect: '全图PageRank计算从72小时缩短至4小时' }
      ],
      deployRecs: [
        { icon: '🏗️', title: '分离实时与离线集群', desc: '实时查询（交易拦截）与批量图计算（风险挖掘）使用独立集群，避免批量作业影响实时服务SLA' },
        { icon: '🔒', title: '数据安全隔离', desc: '图库中存有大量敏感个人信息，需实现字段级加密、访问审计、数据脱敏（模糊化处理供分析使用）' },
        { icon: '📊', title: '建立图质量监控', desc: '监控图的增量接入延迟、节点/边数量异常、算法运行时间等指标，构建图数据质量看板' },
        { icon: '🔄', title: '灰度发布与效果追踪', desc: '新算法/规则先以影子模式运行30天，对比与旧系统的预警差异，验证提升效果后再正式替换' }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 1.8rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 传统局限 */
.limit-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.limit-card { background: #FEF2F2; border-radius: 12px; padding: 20px; border: 1px solid #FCA5A5; }
.limit-icon { font-size: 2rem; margin-bottom: 8px; }
.limit-card h4 { font-size: 14px; font-weight: 700; color: #DC2626; margin-bottom: 8px; }
.limit-problem { font-size: 13px; color: #3D4150; margin-bottom: 8px; line-height: 1.5; }
.limit-example { font-size: 12px; color: #6B7280; margin: 0; line-height: 1.5; }

/* 图模型 */
.model-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.model-card { background: #F6F8FC; border-radius: 12px; padding: 18px; border: 1px solid #E8ECF0; }
.model-icon { font-size: 2rem; margin-bottom: 8px; }
.model-card h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.model-card p { font-size: 13px; color: #5A5E66; margin-bottom: 10px; line-height: 1.5; }
.model-examples { display: flex; flex-wrap: wrap; gap: 4px; }
.model-examples span { background: #EBF3FE; color: #1A73E8; padding: 2px 8px; border-radius: 4px; font-size: 11px; }

/* 算法表 */
.algo-table-wrap { overflow-x: auto; margin: 24px 0; }
.algo-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.algo-table th { background: #0E1F52; color: #fff; padding: 12px 16px; text-align: left; font-weight: 600; }
.algo-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; vertical-align: top; }
.algo-table tr:nth-child(even) td { background: #F6F8FC; }
.algo-cat { font-weight: 600; border-left: 3px solid; padding-left: 12px; }
.algo-name { font-weight: 600; color: #0E1F52; }
.algo-complexity { font-family: monospace; font-size: 12px; color: #8B5CF6; }

/* GNN流程 */
.gnn-flow { display: flex; align-items: center; gap: 0; margin: 24px 0; flex-wrap: wrap; }
.gnn-step { display: flex; align-items: center; gap: 8px; }
.gnn-step-num { width: 28px; height: 28px; background: #1A73E8; color: #fff; border-radius: 50%; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gnn-step-title { font-size: 13px; font-weight: 700; color: #0E1F52; }
.gnn-step-desc { display: none; }
.gnn-arrow { font-size: 20px; color: #1A73E8; margin: 0 4px; }

/* AML实体 */
.aml-entities { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.entity-card { display: flex; gap: 12px; background: #F6F8FC; border-radius: 12px; padding: 16px; border: 1px solid #E8ECF0; align-items: flex-start; }
.entity-icon { width: 40px; height: 40px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0; color: #fff; }
.entity-card h4 { font-size: 13px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.entity-card p { font-size: 12px; color: #5A5E66; margin-bottom: 6px; line-height: 1.4; }
.entity-props { font-size: 11px; color: #8C8C8C; }

/* 场景卡 */
.scenario-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.scenario-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.scenario-header { padding: 14px 16px; color: #fff; display: flex; gap: 8px; align-items: center; }
.scenario-icon { font-size: 1.4rem; }
.scenario-header h4 { font-size: 14px; font-weight: 700; margin: 0; line-height: 1.3; }
.scenario-body { padding: 14px 16px; background: #F6F8FC; }
.scenario-desc { font-size: 13px; color: #3D4150; margin-bottom: 8px; line-height: 1.5; }
.scenario-algo, .scenario-signal { font-size: 12px; color: #5A5E66; margin-bottom: 4px; }

/* 实时vs批量 */
.compare-blocks { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 24px 0; }
.compare-block { border-radius: 12px; padding: 20px; }
.realtime { background: #EBF3FE; border: 1px solid #93C5FD; }
.batch { background: #F0FDF4; border: 1px solid #86EFAC; }
.compare-block-title { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.compare-block ul { margin: 0; padding-left: 18px; }
.compare-block li { font-size: 13px; color: #3D4150; margin-bottom: 6px; line-height: 1.5; }

/* 欺诈关系 */
.fraud-relations { margin: 24px 0; }
.fraud-rel { display: flex; gap: 16px; background: #F6F8FC; border-radius: 12px; padding: 16px 20px; margin-bottom: 10px; border: 1px solid #E8ECF0; align-items: flex-start; }
.rel-icon { font-size: 1.5rem; flex-shrink: 0; }
.fraud-rel h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.fraud-rel p { font-size: 13px; color: #5A5E66; margin-bottom: 6px; line-height: 1.5; }
.rel-risk { font-size: 12px; color: #8C8C8C; display: flex; align-items: center; gap: 3px; }
.risk-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.risk-dot-empty { width: 10px; height: 10px; border-radius: 50%; display: inline-block; background: #E8ECF0; }

/* 欺诈方法 */
.fraud-methods { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.fraud-method { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.method-badge { display: inline-block; color: #fff; padding: 3px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; margin-bottom: 10px; }
.fraud-method h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.fraud-method p { font-size: 13px; color: #5A5E66; margin-bottom: 10px; line-height: 1.5; }
.method-result { font-size: 12px; color: #1A73E8; font-weight: 500; }

/* 保险场景 */
.insurance-scenarios { margin: 24px 0; }
.insurance-item { display: flex; gap: 14px; background: #F6F8FC; border-radius: 10px; padding: 16px; border: 1px solid #E8ECF0; margin-bottom: 10px; align-items: flex-start; }
.ins-icon { font-size: 1.8rem; flex-shrink: 0; }
.insurance-item h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.insurance-item p { font-size: 13px; color: #5A5E66; margin-bottom: 6px; line-height: 1.5; }
.ins-algo { font-size: 12px; color: #8B5CF6; font-weight: 500; }

/* 技术对比 */
.tech-comparison { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.tech-option { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.tech-header { padding: 14px 16px; color: #fff; display: flex; flex-direction: column; gap: 4px; }
.tech-name { font-size: 16px; font-weight: 800; }
.tech-type { font-size: 11px; opacity: 0.8; }
.tech-body { padding: 16px; background: #F6F8FC; }
.pros-label, .cons-label { font-size: 12px; font-weight: 700; display: block; margin-bottom: 4px; }
.pros-label { color: #10B981; }
.cons-label { color: #EA580C; }
.tech-pros, .tech-cons { margin-bottom: 10px; }
.tech-pros ul, .tech-cons ul { margin: 0; padding-left: 16px; }
.tech-pros li, .tech-cons li { font-size: 12px; color: #3D4150; margin-bottom: 3px; line-height: 1.4; }
.tech-use { font-size: 12px; color: #1A73E8; border-top: 1px solid #E8ECF0; padding-top: 8px; }

/* 架构总览 */
.arch-overview { margin: 24px 0; }
.arch-row { border-radius: 12px; padding: 16px 20px; margin-bottom: 12px; border: 1px solid #E8ECF0; }
.arch-row-name { font-size: 15px; font-weight: 700; margin-bottom: 12px; }
.arch-row-comps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.arch-comp { background: rgba(255,255,255,.7); border-radius: 8px; padding: 12px; border: 1px solid #E8ECF0; }
.arch-comp-name { font-size: 13px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.arch-comp-desc { font-size: 12px; color: #5A5E66; line-height: 1.4; }

/* 案例 */
.case-banner { border-radius: 12px; padding: 28px 32px; margin-bottom: 28px; color: #fff; }
.case-banner-tag { font-size: 12px; background: rgba(255,255,255,.2); display: inline-block; padding: 3px 12px; border-radius: 20px; margin-bottom: 10px; }
.case-banner h3 { font-size: 1.4rem; font-weight: 800; margin-bottom: 8px; }
.case-banner p { font-size: 15px; opacity: 0.9; margin: 0; }
.case-banner strong { font-weight: 900; }
.case-section { margin-bottom: 28px; }

/* 痛点 */
.pain-points { margin: 16px 0; }
.pain-point { display: flex; gap: 14px; background: #FEF2F2; border-radius: 10px; padding: 16px; border: 1px solid #FCA5A5; margin-bottom: 10px; align-items: flex-start; }
.pain-num { width: 28px; height: 28px; background: #DC2626; color: #fff; border-radius: 50%; font-size: 14px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pain-point h4 { font-size: 14px; font-weight: 700; color: #DC2626; margin-bottom: 4px; }
.pain-point p { font-size: 13px; color: #3D4150; margin: 0; line-height: 1.5; }

/* 时间线 */
.solution-timeline { margin: 16px 0; position: relative; padding-left: 44px; }
.solution-timeline::before { content: ''; position: absolute; left: 20px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #1A73E8, #0E1F52); border-radius: 2px; }
.timeline-item { position: relative; margin-bottom: 16px; }
.timeline-marker { position: absolute; left: -44px; width: 40px; height: 40px; border-radius: 8px; color: #fff; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; text-align: center; line-height: 1.2; }
.timeline-content { background: #F6F8FC; border-radius: 10px; padding: 14px 18px; border: 1px solid #E8ECF0; }
.timeline-content h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.timeline-content p { font-size: 13px; color: #5A5E66; margin: 0; line-height: 1.5; }

/* 能力统计 */
.capability-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 16px 0; }
.cap-stat { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; }
.cap-value { font-size: 1.6rem; font-weight: 900; margin-bottom: 6px; }
.cap-label { font-size: 13px; color: #5A5E66; }

/* 成果 */
.results-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 16px 0; }
.result-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; }
.result-value { font-size: 1.3rem; font-weight: 900; margin-bottom: 6px; }
.result-metric { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.result-detail { font-size: 12px; color: #5A5E66; line-height: 1.5; }

/* 链路示例 */
.chain-example { display: flex; align-items: center; gap: 0; flex-wrap: wrap; margin: 16px 0; padding: 20px; background: #F6F8FC; border-radius: 12px; border: 1px solid #E8ECF0; justify-content: center; }
.chain-step { display: flex; align-items: center; gap: 0; }
.chain-node { border-radius: 10px; padding: 10px 14px; color: #fff; text-align: center; min-width: 90px; }
.chain-node-icon { font-size: 1.4rem; margin-bottom: 4px; }
.chain-node-label { font-size: 11px; font-weight: 600; line-height: 1.3; }
.chain-edge { display: flex; flex-direction: column; align-items: center; padding: 0 4px; }
.chain-amount { font-size: 10px; color: #5A5E66; }
.chain-arrow { font-size: 18px; color: #1A73E8; }
.chain-note { font-size: 13px; color: #5A5E66; font-style: italic; text-align: center; margin-top: 10px; }

/* 性能挑战 */
.perf-challenges { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.perf-challenge { background: #FEF2F2; border-radius: 12px; padding: 20px; border: 1px solid #FCA5A5; }
.perf-icon { font-size: 2rem; margin-bottom: 8px; }
.perf-challenge h4 { font-size: 14px; font-weight: 700; color: #DC2626; margin-bottom: 6px; }
.perf-challenge p { font-size: 13px; color: #3D4150; margin: 0; line-height: 1.5; }

/* 优化策略 */
.opt-strategies { margin: 24px 0; }
.opt-strategy { border-left: 4px solid; padding: 16px 20px; background: #F6F8FC; border-radius: 0 12px 12px 0; margin-bottom: 12px; }
.opt-header { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.opt-tag { color: #fff; padding: 3px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; }
.opt-header h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin: 0; }
.opt-strategy p { font-size: 13px; color: #5A5E66; margin-bottom: 8px; line-height: 1.5; }
.opt-effect { font-size: 12px; color: #1A73E8; font-weight: 500; }

/* 部署建议 */
.deploy-recs { margin: 24px 0; }
.deploy-rec { display: flex; gap: 16px; background: #F6F8FC; border-radius: 12px; padding: 16px 20px; margin-bottom: 10px; border: 1px solid #E8ECF0; align-items: flex-start; }
.rec-icon { font-size: 1.8rem; flex-shrink: 0; }
.deploy-rec h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.deploy-rec p { font-size: 13px; color: #5A5E66; margin: 0; line-height: 1.5; }

/* CTA */
.cta-section { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); border-radius: 20px; padding: 48px; text-align: center; color: #fff; margin-top: 48px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 1rem; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #fff; color: #0E1F52; padding: 14px 32px; border-radius: 10px; font-weight: 700; text-decoration: none; font-size: 15px; transition: all .2s; }
.btn-primary:hover { background: #EBF3FE; }
.btn-outline { border: 2px solid rgba(255,255,255,.6); color: #fff; padding: 14px 32px; border-radius: 10px; font-weight: 700; text-decoration: none; font-size: 15px; transition: all .2s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,.1); }
</style>
